// A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review.
import { feature, mesh } from "topojson-client";
import { getAbsoluteUrl } from "$utils/urls.js";
import { setContext, getContext, onMount } from "svelte";

/** @typedef {import("$utils/types.d.ts").GeoLevel} GeoLevel */

class MapData {
  stateFeatures = $state(null);
  districtFeatures = $state(null);
  error = $state(null);
  loading = $state(true);
  constructor() {
    let stateUrl = this.getFeatureUrl("state");
    let districtUrl = this.getFeatureUrl("district");
    onMount(async () => {
      try {
        const stateTopoData = await this.fetchData(stateUrl);
        this.stateFeatures = /** @type {any} */ (
          feature(stateTopoData, stateTopoData.objects.states)
        );
      } catch (err) {
        this.error = /** @type {any} */ (err).message || "Failed to load state geo data";
        console.error("Error loading state geo data:", err);
      }
      try {
        const districtTopoData = await this.fetchData(districtUrl);
        this.districtFeatures = /** @type {any} */ (
          feature(districtTopoData, districtTopoData.objects.cds)
        );
      } catch (err) {
        this.error = /** @type {any} */ (err).message || "Failed to load district geo data";
        console.error("Error loading district geo data:", err);
      }
      this.loading = false;
    });
  }

  /**
   * @param {GeoLevel} level
   * @returns {string}
   */
  getFeatureUrl(level) {
    return getAbsoluteUrl(`/data/topo/${level}_topo.json`);
  }

  /**
   * @param {string} url
   * @returns {Promise<any>}
   */
  async fetchData(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  }
}

const contextKey = Symbol("mapContext");

/** Create and register a MapData instance in the component context.
 * @returns {MapData}
 */
export function createMapData() {
  let data = new MapData();
  setContext(contextKey, data);
  return data;
}

/** Retrieve the MapData instance from the component context.
 * @returns {MapData}
 */
export function useMapData() {
  return getContext(contextKey);
}
